import { Link } from "@tanstack/react-router";
import { useState, type FormEvent } from "react";

import { requestPasswordReset } from "../api/client";
import { Button, Card, ErrorNote, Field, Input } from "../ui/primitives";

export function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await requestPasswordReset(email);
      setSent(true);
    } catch (caught) {
      // Only a transport failure or the rate limit lands here. An unknown
      // address is a 202 like any other, by design.
      setError(caught);
    } finally {
      setBusy(false);
    }
  }

  return (
    <main id="main" className="mx-auto flex min-h-dvh max-w-sm flex-col justify-center px-6">
      <h1 className="mb-1 text-xl font-semibold">Reset your password</h1>
      <p className="mb-6 text-sm text-muted">
        We will email you a link to choose a new one.
      </p>

      <Card>
        {sent ? (
          // Worded as a conditional on purpose. "We sent you a link" would be
          // a lie for an address with no account, and a different message for
          // that case would tell anyone typing addresses here who is a
          // customer.
          <p role="status" className="text-sm">
            If <strong>{email}</strong> has an account, a reset link is on its way.
            Check your spam folder if it has not arrived in a few minutes.
          </p>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            {error != null && <ErrorNote error={error} />}

            <Field label="Email" id="email">
              <Input
                id="email"
                type="email"
                autoComplete="username"
                required
                value={email}
                onChange={(event) => setEmail(event.target.value)}
              />
            </Field>

            <Button type="submit" variant="primary" className="w-full" disabled={busy}>
              {busy ? "Sending…" : "Send reset link"}
            </Button>
          </form>
        )}
      </Card>

      <p className="mt-4 text-center text-sm text-muted">
        Remembered it?{" "}
        <Link to="/login" className="text-brand underline">
          Sign in
        </Link>
      </p>
    </main>
  );
}
